import React, { useState, useEffect } from 'react';
import { Grid, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, CircularProgress, Autocomplete, Tooltip } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { useParams } from 'react-router-dom';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import { useDispatch, useSelector } from 'react-redux';
import { PageLoader } from '../../../shared_elements';
import { backendDateFormat, fieldDateFormat } from '../../../constants';
import { trackActivity } from '../../../utils/mixpanel';
import { addReconcileBatchApi, editReconcileBatchApi, getCurrencyApi } from '../apiService';
import { globalGetService, globalPostService, globalPutService } from '../../../utils/globalApiServices';
import { checkApiStatus } from '../../../utils';
import { regexConstants } from '../../../constants/regEx';

const ReconcileBatchCRU = ({ modeType, getResponseBack, onToggleModal, addEditReconcileBatch }) => {
    const dispatch = useDispatch()
    const { leaseSlug } = useParams();
    const { enqueueSnackbar } = useSnackbar();
    const params = { leaseSlug: leaseSlug };
    const [data, setData] = useState(addEditReconcileBatch?.data);
    const [isLoading, setLoading] = useState(false);
    const [error, setError] = useState({});
    const [currencies, setCurrencies] = useState([]);
    const [dropDownLoader, setDropDownLoader] = useState({ currency: false });
    const { leaseBasicDetails } = useSelector((state) => state.leases);

    useEffect(() => {
        getCurrencies();
    }, []);

    const getCurrencies = () => {
        setDropDownLoader(prev => ({ ...prev, currency: true }));
        globalGetService(getCurrencyApi())
            .then(response => {
                if (checkApiStatus(response)) {
                    setCurrencies(response?.data?.data);
                }
                setDropDownLoader(prev => ({ ...prev, currency: false }));
            });
    };

    const invoiceTypeOptions = [
        { label: 'Rental', value: 2 },
        { label: 'Reserve', value: 1 },
    ]

    const onFieldChange = (key, value) => {
        setData({ ...data, [key]: value })
        setError((prev) => ({ ...prev, [key]: '' }));
    };

    const onFocusChange = (key) => { setError({ ...error, [key]: "" }) }

    const onAddEditReconcileBatch = () => {
        let validationInputs = {};

        validationInputs = {
            name: data?.name ? "" : "Please enter Name",
            start_date: data?.start_date ? "" : 'Please enter Start Date',
            end_date: data?.end_date ? "" : 'Please enter End Date',
            invoice_type: data?.invoice_type ? "" : 'Please select Invoice Type',
            currency: data?.currency ? "" : 'Please select Currency',
        };

        const starteDate = moment(data?.start_date);
        const endDate = moment(data?.end_date);
        if (data?.start_date && data?.end_date) {
            if (endDate.isBefore(starteDate)) {
                validationInputs.end_date = "End Date cannot be before Start Date";
            }
            if (starteDate.isAfter(endDate)) {
                validationInputs.start_date = "Start Date cannot be after End Date";
            }
            if (starteDate.isBefore(leaseBasicDetails?.start_date)) {
                validationInputs.start_date = `Start Date cannot be before Lease Start Date`;
            }
            if (starteDate.isAfter(leaseBasicDetails?.end_date)) {
                validationInputs.start_date = `Start Date cannot be after Lease End Date`;
            }
            if (endDate.isAfter(leaseBasicDetails?.end_date)) {
                validationInputs.end_date = "End Date cannot be after Lease End Date";
            }
            if (endDate.isBefore(leaseBasicDetails?.start_date)) {
                validationInputs.end_date = "End Date cannot be before Lease Start Date";
            }
            if (data?.end_date && !endDate.isValid()) {
                validationInputs.end_date = "Please enter valid Start Date";
            }
            if (data?.start_date &&!starteDate.isValid()) {
                validationInputs.start_date = "Please enter valid End Date";
            }
        }

        let payload = Object.assign({}, data)

        payload = {
            name: data?.name || null,
            start_date: data?.start_date || null,
            end_date: data?.end_date || null,
            invoice_type: data?.invoice_type || null,
            currency: data?.currency || null,
            remarks: data?.remarks || null,
        };

        if (Object.keys(validationInputs).every((k) => { return validationInputs[k] === ""; })) {
            setLoading(true);
            if (data.id) {
                globalPutService(editReconcileBatchApi({ ...params, slug: data?.slug }), payload).then(async (response) => {
                    if (checkApiStatus(response)) {
                        enqueueSnackbar(response?.data?.message, { variant: "success", anchorOrigin: { horizontal: "right", vertical: "top" } });
                        onToggleModal()
                        getResponseBack();
                        setLoading(false);
                        trackActivity('Item Edited', { page_title: `Edit Reconcile Batch`, success_msg: response?.data?.message, item_id: data?.id, payload: data, response: response?.data })
                    } else {
                        setLoading(false);
                        enqueueSnackbar(response.data.message, { variant: "error", anchorOrigin: { horizontal: "right", vertical: "top" }, });
                        trackActivity('Item Edited', { page_title: `Edit Reconcile Batch`, error_msg: response?.data?.message, item_id: data?.id, payload: data, response: response?.data })
                    }
                });
            } else {
                globalPostService(addReconcileBatchApi(params), payload).then(async (response) => {
                    if (checkApiStatus(response)) {
                        enqueueSnackbar(response?.data?.message, { variant: "success", anchorOrigin: { horizontal: "right", vertical: "top" }, });
                        onToggleModal()
                        getResponseBack();
                        setLoading(false);
                        trackActivity('Item Added', { page_title: `Add Reconcile Batch`, success_msg: response?.data?.message, item_id: response?.data?.data?.id, payload: data, response: response?.data })
                    } else {
                        setLoading(false);
                        enqueueSnackbar(response?.data?.message, { variant: "error", anchorOrigin: { horizontal: "right", vertical: "top" }, });
                        trackActivity('Item Added', { page_title: `Add Reconcile Batch`, error_msg: response?.data?.message, item_id: null, payload: data, response: response?.data })
                    }
                });
            }
        } else {
            setError(validationInputs);
            trackActivity(`Item ${modeType === 'add' ? 'Added' : 'Edited'}`, { page_title: `${modeType === 'add' ? 'Add' : modeType === 'edit' ? 'Edit' : 'View'} Reconcile Invoice Batch`, error_msg: validationInputs, })
        }
    };
    const handleItemClicked = (page_type) => {
        trackActivity('Item Clicked', { page_title: `${modeType === 'add' ? 'Add' : modeType === 'edit' ? 'Edit' : 'View'} Reconcile Invoice Batch`, page_type: page_type, })
    }

    return (
        <>
            <Dialog
                aria-labelledby="customized-dialog-title"
                className='add-lease-dialog'
                maxWidth='md'
                fullWidth
                open={true}
            >
                <DialogTitle sx={{ m: 0, p: 2, pl:3 }} className='dialog-header'>
                    {modeType === 'add' ? "Add Reconcile Invoices Batch" : 'Edit  Reconcile Invoices Batch'}
                </DialogTitle>
                <DialogContent dividers >
                    <Grid container spacing={2}>
                        <Grid size={{ md: 4, xs: 12 }}>
                            <TextField
                                disabled={modeType === "view"}
                                id="name"
                                label="Name"
                                variant="outlined"
                                size='small'
                                value={data?.name ? data?.name : ''}
                                onChange={(e) => { regexConstants.restrictUnicodeCharacters.test(e.target.value) || e.target.value === "" ? onFieldChange('name', e.target.value.trimStart()) : e.preventDefault() }}
                                required
                                margin='normal'
                                fullWidth
                                slotProps={{ inputLabel: { shrink: true }, htmlInput: { maxLength: 50 } }}
                                onFocus={() => onFocusChange("name")}
                                error={error?.name}
                                helperText={error?.name || ''}
                            />
                        </Grid>
                        <Grid size={{ md: 4, xs: 12 }}>
                            <LocalizationProvider dateAdapter={AdapterMoment}>
                                <DatePicker
                                    label="Start Date"
                                    value={data?.start_date ? moment(data?.start_date) : null}
                                    minDate={leaseBasicDetails?.start_date ? moment(leaseBasicDetails.start_date) : undefined}
                                    maxDate={leaseBasicDetails?.end_date ? moment(leaseBasicDetails.end_date) : undefined}
                                    onChange={(data) => {
                                        const isValid = moment(data)?.isValid();
                                        onFieldChange("start_date", data ? moment(data).format(backendDateFormat) : null);
                                        if (isValid) {
                                            onFocusChange("start_date");
                                        }
                                    }}
                                    slotProps={{
                                        textField: {
                                            required: "true",
                                            InputLabelProps: { shrink: true },
                                            size: "small",
                                            margin: "normal",
                                            error: error.start_date || false,
                                            helperText: error.start_date || '',
                                            onFocus: () => {
                                                if (error.start_date) {
                                                    onFocusChange("start_date");
                                                }
                                            }
                                        },
                                        field: {
                                            clearable: true,
                                            onClear: () => {
                                                onFieldChange("start_date", null);
                                                onFocusChange("start_date");
                                            }
                                        }
                                    }}
                                    sx={{ width: "100%" }}
                                    format={fieldDateFormat}
                                />
                            </LocalizationProvider>
                        </Grid>
                        <Grid size={{ md: 4, xs: 12 }}>
                            <LocalizationProvider dateAdapter={AdapterMoment}>
                                <DatePicker
                                    label="End Date"
                                    value={data?.end_date ? moment(data?.end_date) : null}
                                    minDate={data?.start_date ? moment(data.start_date) : leaseBasicDetails?.start_date ? moment(leaseBasicDetails.start_date) : undefined}
                                    maxDate={leaseBasicDetails?.end_date ? moment(leaseBasicDetails.end_date) : undefined}
                                    onChange={(data) => {
                                        const isValid = moment(data)?.isValid();
                                        onFieldChange("end_date", data ? moment(data).format(backendDateFormat) : null);
                                        if (isValid) {
                                            onFocusChange("end_date");
                                        }
                                    }}
                                    slotProps={{
                                        textField: {
                                            required: "true",
                                            InputLabelProps: { shrink: true },
                                            size: "small",
                                            margin: "normal",
                                            error: error.end_date || false,
                                            helperText: error.end_date || '',
                                            onFocus: () => {
                                                if (error.end_date) {
                                                    onFocusChange("end_date");
                                                }
                                            }
                                        },
                                        field: {
                                            clearable: true,
                                            onClear: () => {
                                                onFieldChange("end_date", null);
                                                onFocusChange("end_date");
                                            }
                                        }
                                    }}
                                    sx={{ width: "100%" }}
                                    format={fieldDateFormat}
                                />
                            </LocalizationProvider>
                        </Grid>
                        <Grid size={{ md: 4, xs: 12 }}>
                            <Autocomplete
                                disablePortal
                                disableClearable
                                disabled={modeType === 'view'}
                                options={invoiceTypeOptions}
                                getOptionLabel={opt => opt.label || ''}
                                value={invoiceTypeOptions.find(opt => opt.value === data?.invoice_type) || null}
                                isOptionEqualToValue={(option, value) => option.value === value.value}
                                onChange={(e, value) => onFieldChange('invoice_type', value?.value ?? null)}
                                renderInput={params => (
                                    <TextField {...params} label="Invoice Type" required size="small" margin="normal"
                                        error={!!error.invoice_type}
                                        helperText={error.invoice_type || ''}
                                        slotProps={{ inputLabel: { shrink: true } }}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid size={{ md: 4, xs: 12 }}>
                            <Autocomplete
                                disablePortal
                                disableClearable
                                disabled={modeType === 'view' || dropDownLoader.currency}
                                options={currencies}
                                value={currencies.find(c => c.code === data?.currency) || null}
                                noOptionsText={dropDownLoader.currency ? "Loading..." : "No Option Available"}
                                getOptionLabel={(option) => option?.name || ""}
                                onFocus={() => setError(prev => ({ ...prev, currency: "" }))}
                                onChange={(e, newValue) => onFieldChange('currency', newValue?.code || null)}
                                isOptionEqualToValue={(option, value) => option.code === value.code}
                                renderInput={(params) => {
                                    const selectedLabel = params?.inputProps?.value || "";
                                    return (
                                        <Tooltip title={selectedLabel.length > 23 ? selectedLabel : ""} placement="bottom" arrow>
                                            <TextField
                                                required
                                                {...params}
                                                label="Currency"
                                                margin='normal'
                                                size='small'
                                                error={Boolean(error.currency)}
                                                helperText={error.currency}
                                                slotProps={{
                                                    inputLabel: { shrink: true },
                                                    input: {
                                                        ...params?.InputProps,
                                                        endAdornment: (
                                                            <>
                                                                {dropDownLoader?.currency ? <CircularProgress color="inherit" size={20} /> : null}
                                                                {params?.InputProps?.endAdornment}
                                                            </>
                                                        )
                                                    }
                                                }}
                                            />
                                        </Tooltip>
                                    );
                                }}
                            />
                        </Grid>
                        <Grid size={{ md: 12, xs: 12 }}>
                            <TextField
                                disabled={modeType === "view"}
                                id="remarks"
                                label="Remarks"
                                variant="outlined"
                                size='small'
                                margin='normal'
                                value={data?.remarks || ''}
                                onChange={(e) => {
                                    regexConstants.restrictUnicodeCharacters.test(e.target.value) || e.target.value === "" ? onFieldChange("remarks", e.target.value.trimStart()) : e.preventDefault()
                                }}
                                slotProps={{ inputLabel: { shrink: true }}}
                                sx={{ '& .MuiInputBase-input': { resize: 'vertical', overflow: 'auto' } }}
                                fullWidth
                                multiline
                                rows={3}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ marginRight: "15px" }}>
                    <Button variant='outlined' size='small' disabled={isLoading} className="cancel-btn" onClick={() => { onToggleModal(); handleItemClicked('Cancel Button') }}>
                        Cancel
                    </Button>
                    <Button disabled={isLoading || modeType === "view"} size='small' variant='contained' className='btn-primary' onClick={() => { handleItemClicked('Save Button'); onAddEditReconcileBatch() }}>
                        {isLoading ? <CircularProgress size={24} /> : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>
            {isLoading ? <PageLoader /> : null}
        </>

    )
}
export default ReconcileBatchCRU;